import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { UiErrorParser } from './ui-error-parser';

export const UI_ERROR_PARSERS = new InjectionToken<UiErrorParser[]>('UI_ERROR_PARSERS');

export type UIErrorMessage = {
    text: string;
    type: 'auth' | 'arrecadacao' | 'medico' | 'pessoa' | 'prestador' | 'comissoes' | 'unknown';
    url: string;
    method: string;
    ignore: boolean;
    title?: string;
    severity?: string;
};

@Injectable()
export class UiErrorMessageBuilder {
    constructor(@Optional() @Inject(UI_ERROR_PARSERS) public parsers: UiErrorParser[] = []) {}

    buildErrorMessage(error: HttpErrorResponse, req: HttpRequest<unknown>): UIErrorMessage {
        for (const parser of this.parsers) {
            if (parser.verifyContext(error, req)) {
                return parser.parseError(error, req);
            }
        }

        const detail = this.extractErrorDetail(error);
        if (detail) {
            const isClientBusinessError = error.status >= 400 && error.status < 500;
            return {
                text: detail,
                type: 'unknown',
                method: req.method,
                url: req.url,
                ignore: false,
                severity: isClientBusinessError ? 'warn' : 'error',
                title: isClientBusinessError ? 'Atenção' : 'Erro'
            };
        }

        return {
            text: '',
            type: 'unknown',
            method: req.method,
            url: req.url,
            ignore: true
        };
    }

    private extractErrorDetail(error: HttpErrorResponse): string {
        const raw = error?.error;
        let payload: Record<string, unknown> | null = null;

        if (raw && typeof raw === 'object') {
            payload = raw as Record<string, unknown>;
        } else if (typeof raw === 'string' && raw.trim()) {
            try {
                const parsed = JSON.parse(raw);
                payload = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : { detail: raw };
            } catch {
                payload = { detail: raw };
            }
        }

        const detail = this.normalizeMessage(payload?.['detail']);
        if (detail) {
            return detail;
        }

        const message = this.normalizeMessage(payload?.['message']);
        if (message) {
            return message;
        }

        // Último recurso: CfmErrorDto que carrega a mensagem só no `title` (ex.: regra de negócio
        // 4xx com `errors` nulo → `detail` vazio). Sem isto o erro seria silenciosamente engolido.
        const title = this.normalizeMessage(payload?.['title']);
        if (title) {
            return title;
        }

        return '';
    }

    private normalizeMessage(value: unknown): string {
        if (value == null) {
            return '';
        }

        if (Array.isArray(value)) {
            const texts = value.map((item) => this.normalizeMessage(item)).filter(Boolean);
            return texts.join('\n');
        }

        const text = String(value).trim();
        if (!text) {
            return '';
        }

        if (text.startsWith('[') && text.endsWith(']')) {
            const inner = text.slice(1, -1).trim();
            return this.fixMessageAccents(inner.replace(/^["']+|["']+$/g, '').trim());
        }

        return this.fixMessageAccents(text);
    }

    private fixMessageAccents(text: string): string {
        const normalized = text.trim().toLowerCase();
        if (normalized === 'municipio ja esta vinculado a outra delegacia') {
            return 'Município já está vinculado a outra delegacia';
        }
        return text;
    }
}
